import { useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { Honeypot, inputClass } from "@/components/Shared";
import { registerInterest } from "@/lib/api";
import { trackEvent, leadContext } from "@/lib/analytics";

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];

export const RegisterForm = () => {
  const [params] = useSearchParams();
  const { pathname, search } = useLocation();
  const { register, handleSubmit, formState: { isSubmitting } } = useForm();
  const [done, setDone] = useState(false);
  const utm = Object.fromEntries(UTM_KEYS.map((k) => [k, params.get(k) || undefined]));

  const onSubmit = async (fields) => {
    try {
      const lead = leadContext("priority_list_join", utm.utm_source || "direct");
      await registerInterest({ ...fields, priority: !!fields.priority, ...utm, page: pathname + search, meta: lead });
      trackEvent("priority_list_join", { source: utm.utm_source || "direct", priority: !!fields.priority }, lead);
      setDone(true);
    } catch {
      toast.error("Something went wrong. Please try again or email info@lumiereaesthetics.com.au.");
    }
  };

  if (done) {
    return (
      <div className="relative" data-testid="register-success">
        <p className="eyebrow">The Lumière list</p>
        <h2 className="text-deep-sea mt-4">Thank you — you're on the list.</h2>
        <p className="text-ink/80 mt-5 leading-[1.85]">We'll be in touch occasionally, by email.</p>
        <Link to="/" className="link-sea text-sm inline-block mt-8" data-testid="register-home-link">Back to the homepage</Link>
      </div>
    );
  }

  return (
    <div className="relative">
      <h2 className="text-deep-sea" data-testid="register-heading">Join the list</h2>
      <form onSubmit={handleSubmit(onSubmit)} className="mt-7 space-y-4" data-testid="register-form" noValidate>
        <Honeypot register={register} />
        {UTM_KEYS.map((k) => utm[k] && <input key={k} type="hidden" name={k} value={utm[k]} data-testid={`register-${k}`} readOnly />)}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="reg-first" className="eyebrow block mb-2">First name</label>
            <input id="reg-first" {...register("first_name", { required: true })} autoComplete="given-name" required className={inputClass} data-testid="register-first-name-input" />
          </div>
          <div>
            <label htmlFor="reg-last" className="eyebrow block mb-2">Last name</label>
            <input id="reg-last" {...register("last_name", { required: true })} autoComplete="family-name" required className={inputClass} data-testid="register-last-name-input" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="reg-phone" className="eyebrow block mb-2">Mobile</label>
            <input id="reg-phone" type="tel" {...register("phone", { required: true })} autoComplete="tel" required className={inputClass} data-testid="register-phone-input" />
          </div>
          <div>
            <label htmlFor="reg-email" className="eyebrow block mb-2">Email</label>
            <input id="reg-email" type="email" {...register("email", { required: true })} autoComplete="email" required className={inputClass} data-testid="register-email-input" />
          </div>
        </div>
        <label className="flex items-start gap-3 text-ink/80 text-sm leading-relaxed cursor-pointer pt-1">
          <input type="checkbox" {...register("consent", { required: true })} required className="mt-1 accent-[#A68A5B]" data-testid="register-consent-checkbox" />
          <span>I'd like to receive Lumière news, journal articles and invitations by email and SMS. I can unsubscribe at any time.</span>
        </label>
        <p className="text-soft-grey text-xs leading-relaxed" data-testid="register-collection-notice">
          We collect this to keep you updated and, if you've asked, to offer you an opening appointment. See our{" "}
          <Link to="/privacy" className="underline underline-offset-2 hover:text-gold transition-colors">Privacy Policy</Link>.
        </p>
        <button type="submit" disabled={isSubmitting} className="btn-gold w-full py-4 text-sm font-medium tracking-[0.06em] disabled:opacity-60" data-testid="register-submit-button">
          {isSubmitting ? "Sending…" : "Join the list"}
        </button>
      </form>
    </div>
  );
};

export default RegisterForm;
